import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { ChangeEvent, useRef, useEffect, memo, FocusEvent } from 'react';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { useField, useFormikContext } from 'formik';
import { PricePatternMask } from '../PricePatternMask/PricePatternMask';
import { WebFormPriceFieldProps } from '../FormPriceField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormPriceField = memo(({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required = false,
    autoFocus,
    capsLock,
    fullWidth = false,
    seventyFivePercentWidth = false,
    tabFocusRefContext,
    externalRef,
    onChangeCallback,
    onBlurCallback,
    onFocusCallback,
}: WebFormPriceFieldProps) => {
    /**
     * Not all formik validation accounts for TextFields that are null.
     * So even though we pass an undefined validation type to the function,
     * it is still required in order to have null required fields validate.
     */
    const [field, meta] = useField<string>({
        name,
        validate: TextFieldValidation({
            required: required ?? false,
            label,
            name,
            validationType: undefined,
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const locale = useLocale();
    const {
        dealer: { primaryLanguage },
    } = useKioskState();

    useEffect(() => {
        /**
         * Since the price field has external uses that use a normal ref,
         * we need to ensure that the external ref stays in sync with the
         * internal ref we use to manage the controls state.
         */
        if (externalRef && externalRef.current && inputRef.current) {
            externalRef.current = inputRef.current;
        }
    }, [externalRef]);

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    const handleOnBlur = () => {
        onBlurCallback && onBlurCallback();
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFieldValue(name, event.target.value);
        onChangeCallback && onChangeCallback(event.target.value);
    };

    const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onFocusCallback && onFocusCallback();
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'decimal'}
            type={'text'}
            inputRef={inputRef}
            value={field.value ?? ''}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            onFocus={handleOnFocus}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputProps={{
                inputComponent: PricePatternMask as any,
            }}
        />
    );
});
